import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BasicUserInfo, PASSWORD_ERROR_KEYS, UserInfoStateStructure } from '@ptl/account-management';
import { environment } from 'src/environments/environment';

export class CustomValidators {
  static passwordMatchValidator(control: AbstractControl) {
    const password: string = control.get('newPassword').value; // get password from our password form control
    const confirmPassword: string = control.get('reenterNewPassword').value; // get password from our confirmPassword form control
    // compare is the password math
    if (CustomValidators.constantTimeCompare(password, confirmPassword)) {
      // if they don't match, set an error in our confirmPassword form control
      control.get('reenterNewPassword').setErrors({ NoPassswordMatch: true });
    }
  }

  static constantTimeCompare(a: string, b: string): boolean {
    if (a.length !== b.length) {
      return false;
    }

    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return result === 0;
  }

  static currPwdValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control && control.get('newPassword') && control.get('reenterNewPassword')) {
        // get new password and confirm password from our password form control
        let newPasswordControl = control.get('newPassword');
        let reenterPasswordControl = control.get('reenterNewPassword');

        // check if new passwords match
        CustomValidators._validatePasswordError(
          reenterPasswordControl,
          (newPasswordControl.value !== reenterPasswordControl.value),
          PASSWORD_ERROR_KEYS.NEW_PASSWORDS_DO_NOT_MATCH);
      }

      return null;
    };
  }

  static oldPasswordDiffNewPasswordValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control && control.get('currentPassword') && control.get('newPassword')) {
        // get current password and new password from our password form control
        let currentPasswordControl = control.get('currentPassword');
        let newPasswordControl = control.get('newPassword');

        // check if current password is the same as the new password
        CustomValidators._validatePasswordError(
          newPasswordControl,
          (currentPasswordControl.value && currentPasswordControl.value === newPasswordControl.value),
          PASSWORD_ERROR_KEYS.OLD_PASSWORD_MATCHES_NEW);
      }

      return null;
    };
  }

  static newPasswordDomainAndUserNameValidator(logedInUserBasicInfo: BasicUserInfo): ValidatorFn {
    return (control: AbstractControl) => {
      if (control && control.get('newPassword') && control.get('newPassword').value != null) {
        // get new password from our password form control
        let newPasswordControl = control.get('newPassword');

        // check if current password contains contains userName or applicationName

        // check for User name
        let isUserNamePresent = (newPasswordControl.value.toUpperCase().includes(logedInUserBasicInfo.firstName.toUpperCase()) ||
          newPasswordControl.value.toUpperCase().includes(logedInUserBasicInfo.lastName.toUpperCase()));

        // check for App name
        let isAppNamePresent = newPasswordControl.value.toUpperCase().includes(environment.infrastructure.deploymentDomain.split(".")[0].toUpperCase());

        CustomValidators._validatePasswordError(
          newPasswordControl,
          (isUserNamePresent || isAppNamePresent),
          PASSWORD_ERROR_KEYS.NEW_PASSWORD_CONTAIN_DOMAIN_OR_USER_NAME);
      }

      return null;
    };
  }

  static valueExactlyMatchesInput(value: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && control.value.length > 0) {
        if (control.value === value) { return null; }
        else {
          return { valueExactlyMatchesInput: true };
        }
      } else {
        return null;
      }
    }
  }

  static _validatePasswordError(setErrorControl: AbstractControl, condition: boolean, errorKey: string): void {
    if (condition) {
      // Make sure errors object exists
      setErrorControl.setErrors(setErrorControl.errors ? setErrorControl.errors : {});
      // Add the new error
      setErrorControl.errors[errorKey] = true;
    }
    // if the condition is not met clear the error if needed
    else if (setErrorControl.errors) {
      delete setErrorControl.errors[errorKey];

      // Wipe errors completely if no error keys are left
      // The framework validation recognizes an empty object as an error so it has to be set to null
      if (!Object.keys(setErrorControl.errors).length) {
        setErrorControl.setErrors(null);
      }
    }
  }

  static phoneValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const phone = control.value;
      const formattedPhone = phone != null ? phone.replace(/[- )(]/g, '') : '';
      if (formattedPhone.length === 0 || formattedPhone.length === 10) {
        return null;
      } else {
        return { phoneError: true };
      }

    };
  }

  static noCommonSecurityAnswers(logedInUserBasicInfo: UserInfoStateStructure): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const securityAns = control?.value;
      if (control && securityAns != null) {
        const currentPassword = control.parent?.get('currentPassword')?.value;
        const commonAnswers = ['password']; // Add common answers here
        const isCommon = commonAnswers.includes(securityAns?.toLowerCase());
        const domain = window.location.hostname.split('.')[1];
        const email = logedInUserBasicInfo.userInfo.email;
        const userFirstName = logedInUserBasicInfo.basicUserInfo.firstName;
        const userLastName = logedInUserBasicInfo.basicUserInfo.lastName;
        if ((securityAns.length >= 3) && CustomValidators.isConsecutiveOrSequence(securityAns)) {
          return { consecutiveCharacters: true }; // Return an error if consecutive characters are found
        }
        if (securityAns.toLocaleLowerCase().includes(domain.toLocaleLowerCase())
          || securityAns.toLocaleLowerCase().includes(userFirstName.toLocaleLowerCase())
          || securityAns.toLocaleLowerCase().includes(userLastName.toLocaleLowerCase())
          || securityAns.toLocaleLowerCase().includes(email.toLocaleLowerCase())
          || securityAns.toLocaleLowerCase() === currentPassword?.toLocaleLowerCase()
          || isCommon) {
          return { securityAnswerContainDomainOrUserName: true };
        }
        return null;
      };
    }
  }

  static isConsecutiveOrSequence(input): boolean {
    // Check for consecutive repetitions using regular expressions
    // example for valid consecutive numberic and alphabet => ["111", "8888", "ddd", "kkkk"] and invalid values ["9111", "78888", "kddd"]
    if (input.split('').every((char) => char === input[0])) {
      return true;
    }

    // Check for sequences (numeric and alphabetic).
    // Sequences: The code now efficiently checks for both numeric and alphabetic sequences. 
    // It iterate and compare each character/digit with the expected value in the sequence. ^[a-zA-Z]+$ and ^\d+$ are used 
    // to ensure that we only check sequences that are only alphabetic or numeric respectively. 
    // This prevents false positives like "1a2b" being identified as a sequence. 
    // example for valid sequential numberic and alphabet => ["4567", "1234", "abcd", "efg"] and invalid values ["45679", "71234", "zabcd", "efgd"]
    if (input.length > 1) {
      const isNumericSequence = /^\d+$/.test(input) &&
        [...input].every((digit, index) =>
          parseInt(digit) === parseInt(input[0]) + index);

      const isAlphabeticSequence = /^[a-zA-Z]+$/.test(input) &&
        [...input].every((char, index) => {
          const charCode = input.charCodeAt(0);
          return input.charCodeAt(index) === charCode + index;
        });

      if (isNumericSequence || isAlphabeticSequence) {
        return true;
      }
    }
    return false;
  }

}
